import { FC, useEffect, useRef, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Property, InvestorProfile } from '@/types/index';
import { investorProfileService } from '@services/investor-profile.service';
import { propertyService } from '@services/property.service';

interface Props {
  property: Property;
}

const DEFAULTS = {
  flipFacteurVente: 0.65,
  flipBudgetCuisine: 15000,
  flipBudgetSalleBain: 12000,
  flipCoutMenuiserie: 2000,
  flipCoutSol: 80,
  flipCoutElectricite: 50,
  flipMargeVoulue: 10,
  flipFraisAchat: 5,
  flipFraisRevente: 3,
  flipFraisEmprunt: 2,
};

function fmtChf(v: number) {
  return new Intl.NumberFormat('de-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v) + ' CHF';
}

function KpiChip({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 text-center">
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">{label}</p>
      <p className={`text-sm font-bold font-mono leading-tight ${color}`}>{value}</p>
    </div>
  );
}

function NumInput({ label, value, onChange, unit, step = 1 }: {
  label: string; value: number; onChange: (v: number) => void; unit?: string; step?: number;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-gray-600 dark:text-gray-400 flex-1">{label}</span>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          step={step}
          value={value}
          onChange={e => { const n = parseFloat(e.target.value); if (!isNaN(n)) onChange(n); }}
          className="w-28 px-2 py-1.5 text-sm text-right border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:outline-none"
        />
        {unit && <span className="text-xs text-gray-400 w-14">{unit}</span>}
      </div>
    </div>
  );
}

function Row({ label, value, bold, color, indent }: {
  label: string; value: string; bold?: boolean; color?: string; indent?: boolean;
}) {
  return (
    <div className={`flex justify-between items-center py-2 ${indent ? 'pl-4' : ''}`}>
      <span className={`text-sm ${bold ? 'font-semibold text-gray-800 dark:text-gray-200' : 'text-gray-600 dark:text-gray-400'}`}>
        {label}
      </span>
      <span className={`text-sm font-mono ${bold ? 'font-bold' : ''} ${color ?? 'text-gray-800 dark:text-gray-200'}`}>
        {value}
      </span>
    </div>
  );
}

function AccordionHeader({ title, open, onToggle }: { title: string; open: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
    >
      <span>{title}</span>
      <svg
        className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        fill="none" stroke="currentColor" viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
}

export const FlipAnalysisTab: FC<Props> = ({ property }) => {
  const [, setProfile] = useState<Partial<InvestorProfile>>({});

  const [facteur,      setFacteur]      = useState(DEFAULTS.flipFacteurVente);
  const [cuisine,      setCuisine]      = useState(DEFAULTS.flipBudgetCuisine);
  const [salleBain,    setSalleBain]    = useState(DEFAULTS.flipBudgetSalleBain);
  const [menuiserie,   setMenuiserie]   = useState(DEFAULTS.flipCoutMenuiserie);
  const [sol,          setSol]          = useState(DEFAULTS.flipCoutSol);
  const [electricite,  setElectricite]  = useState(DEFAULTS.flipCoutElectricite);
  const [margeVoulue,  setMargeVoulue]  = useState(DEFAULTS.flipMargeVoulue);
  const [fraisAchat,   setFraisAchat]   = useState(DEFAULTS.flipFraisAchat);
  const [fraisRevente, setFraisRevente] = useState(DEFAULTS.flipFraisRevente);
  const [fraisEmprunt, setFraisEmprunt] = useState(DEFAULTS.flipFraisEmprunt);

  const [profileLoaded, setProfileLoaded] = useState(false);
  const [showParams, setShowParams] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const marketPerM2 = property.marketPricePerM2 ? Math.round(property.marketPricePerM2) : null;
  const savedPrixMarche = property.flipPrixMarcheM2 ? Math.round(property.flipPrixMarcheM2) : null;
  const [prixMarche, setPrixMarche] = useState<number>(savedPrixMarche ?? marketPerM2 ?? 0);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handlePrixMarcheChange = (v: number) => {
    setPrixMarche(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      propertyService.updateSelection(property.id, { flipPrixMarcheM2: v }).catch(() => {});
    }, 800);
  };

  // Sauvegarde cross-device dans PropertySelection (DB) — debounce 800ms, 1 seul appel API
  const paramsSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!profileLoaded) return;
    if (paramsSaveRef.current) clearTimeout(paramsSaveRef.current);
    paramsSaveRef.current = setTimeout(() => {
      propertyService.updateSelection(property.id, {
        flipFacteurVente: facteur,
        flipBudgetCuisine: cuisine,
        flipBudgetSalleBain: salleBain,
        flipCoutMenuiserie: menuiserie,
        flipCoutSol: sol,
        flipCoutElectricite: electricite,
        flipMargeVoulue: margeVoulue,
        flipFraisAchat: fraisAchat,
        flipFraisRevente: fraisRevente,
        flipFraisEmprunt: fraisEmprunt,
      }).catch(() => {});
    }, 800);
  }, [facteur, cuisine, salleBain, menuiserie, sol, electricite,
      margeVoulue, fraisAchat, fraisRevente, fraisEmprunt, property.id, profileLoaded]);

  useEffect(() => {
    investorProfileService.get().then(p => {
      setProfile(p);
      // Priorité : PropertySelection DB > profil investisseur > DEFAULTS
      if (property.flipFacteurVente    != null) setFacteur(property.flipFacteurVente);
      else if (p.flipFacteurVente      != null) setFacteur(p.flipFacteurVente);

      if (property.flipBudgetCuisine   != null) setCuisine(property.flipBudgetCuisine);
      else if (p.flipBudgetCuisine     != null) setCuisine(p.flipBudgetCuisine);

      if (property.flipBudgetSalleBain != null) setSalleBain(property.flipBudgetSalleBain);
      else if (p.flipBudgetSalleBain   != null) setSalleBain(p.flipBudgetSalleBain);

      if (property.flipCoutMenuiserie  != null) setMenuiserie(property.flipCoutMenuiserie);
      else if (p.flipCoutMenuiserie    != null) setMenuiserie(p.flipCoutMenuiserie);

      if (property.flipCoutSol         != null) setSol(property.flipCoutSol);
      else if (p.flipCoutSol           != null) setSol(p.flipCoutSol);

      if (property.flipCoutElectricite != null) setElectricite(property.flipCoutElectricite);
      else if (p.flipCoutElectricite   != null) setElectricite(p.flipCoutElectricite);

      if (property.flipMargeVoulue     != null) setMargeVoulue(property.flipMargeVoulue);
      else if (p.flipMargeVoulue       != null) setMargeVoulue(p.flipMargeVoulue);

      if (property.flipFraisAchat      != null) setFraisAchat(property.flipFraisAchat);
      else if (p.flipFraisAchat        != null) setFraisAchat(p.flipFraisAchat);

      if (property.flipFraisRevente    != null) setFraisRevente(property.flipFraisRevente);
      else if (p.flipFraisRevente      != null) setFraisRevente(p.flipFraisRevente);

      if (property.flipFraisEmprunt    != null) setFraisEmprunt(property.flipFraisEmprunt);
      else if (p.flipFraisEmprunt      != null) setFraisEmprunt(p.flipFraisEmprunt);

      setProfileLoaded(true);
    }).catch(() => { setProfileLoaded(true); });
  }, [property.id]);

  const surface   = property.area        ?? 0;
  const rooms     = property.rooms       ?? 0;
  const prixAchat = property.askingPrice ?? 0;

  // Calculs — prix de vente
  const prixVentePotentiel  = Math.round(surface * prixMarche * facteur);
  const fraisReventeMontant = Math.round(prixVentePotentiel * (fraisRevente / 100));
  const prixVenteNet        = prixVentePotentiel - fraisReventeMontant;

  // Travaux de rénovation
  const coutMenuiserieTotal = Math.round(menuiserie * rooms);
  const coutSolTotal        = Math.round(sol        * surface);
  const coutElecTotal       = Math.round(electricite * surface);
  const totalRenovation     = cuisine + salleBain + coutMenuiserieTotal + coutSolTotal + coutElecTotal;

  // Marge souhaitée — % du prix de vente potentiel
  const margeSouhaitee = prixVentePotentiel > 0 ? Math.round(prixVentePotentiel * margeVoulue / 100) : 0;

  // Résolution algébrique de la circularité frais d'achat
  const fraisEmpruntMontant = Math.round(totalRenovation * (fraisEmprunt / 100));
  const prixAchatMax = prixVenteNet > 0
    ? Math.round(
        (prixVenteNet - margeSouhaitee - totalRenovation * (1 + fraisEmprunt / 100))
        / (1 + fraisAchat / 100)
      )
    : null;
  const fraisAchatMontant    = prixAchatMax != null ? Math.round(prixAchatMax * fraisAchat / 100) : 0;
  const totalFraisFinanciers = fraisAchatMontant + fraisEmpruntMontant;
  const totalCouts           = totalRenovation + margeSouhaitee + totalFraisFinanciers;

  const totalInvesti = prixAchatMax != null
    ? prixAchatMax + fraisAchatMontant + totalRenovation + fraisEmpruntMontant : 0;
  const roi = totalInvesti > 0 ? (margeSouhaitee / totalInvesti) * 100 : 0;
  const ecartPrix = prixAchatMax != null && prixAchatMax > 0
    ? ((prixAchat - prixAchatMax) / prixAchatMax) * 100 : null;

  // Code couleur 3 états
  const flipStatus: 'green' | 'orange' | 'red' =
    ecartPrix === null  ? 'red'
    : ecartPrix <= 0    ? 'green'
    : ecartPrix <= 20   ? 'orange'
    :                     'red';

  const colorClass = {
    green:  'text-emerald-600 dark:text-emerald-400',
    orange: 'text-amber-500 dark:text-amber-400',
    red:    'text-red-600 dark:text-red-400',
  }[flipStatus];

  const bgClass = {
    green:  'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400',
    orange: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400',
    red:    'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400',
  }[flipStatus];

  const signalText = {
    green:  '🟢 Flip directement réalisable au prix affiché',
    orange: '🟠 Négociation nécessaire — écart réaliste (PMA réaliste)',
    red:    '🔴 Écart trop important — négociation improbable (PMA irréaliste)',
  }[flipStatus];

  const missingData = surface === 0 || prixAchat === 0 || prixMarche === 0;

  // Donut 1 — composition du prix de vente potentiel
  const donutData = prixAchatMax != null && prixAchatMax > 0 ? [
    { name: "Prix d'achat max", value: prixAchatMax,         fill: '#3b82f6' },
    { name: 'Travaux',          value: totalRenovation,      fill: '#f97316' },
    { name: 'Marge souhaitée',  value: margeSouhaitee,       fill: '#10b981' },
    { name: 'Frais financiers', value: totalFraisFinanciers, fill: '#6b7280' },
    { name: 'Frais de courtage', value: fraisReventeMontant, fill: '#8b5cf6' },
  ].filter(d => d.value > 0) : [];

  // Donut 2 — composition des frais de rénovation
  const donutRenovData = totalRenovation > 0 ? [
    { name: 'Cuisine',     value: cuisine,            fill: '#f59e0b' },
    { name: 'Salle de bain', value: salleBain,        fill: '#3b82f6' },
    { name: 'Menuiserie',  value: coutMenuiserieTotal, fill: '#a16207' },
    { name: 'Sol',         value: coutSolTotal,        fill: '#14b8a6' },
    { name: 'Électricité', value: coutElecTotal,       fill: '#eab308' },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-4">

      {missingData && (
        <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-400">
          ⚠ Données manquantes : {surface === 0 ? 'surface ' : ''}{prixAchat === 0 ? "prix d'achat " : ''}{prixMarche === 0 ? 'prix marché/m²' : ''} — ouvrez les paramètres pour saisir le prix marché.
        </div>
      )}

      {/* ── Carte résumé visuel ─────────────────────────────────── */}
      <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 space-y-4">

        {/* 4 KPI chips */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiChip
            label="Prix d'achat max (PMA)"
            value={prixAchatMax != null ? fmtChf(prixAchatMax) : '—'}
            color={colorClass}
          />
          <KpiChip
            label="Marge prévue"
            value={margeSouhaitee > 0 ? fmtChf(margeSouhaitee) : '—'}
            color={colorClass}
          />
          <KpiChip
            label="ROI"
            value={roi > 0 ? `${roi.toFixed(1)} %` : '—'}
            color={colorClass}
          />
          <KpiChip
            label="Écart prix vs PMA"
            value={ecartPrix != null ? `${ecartPrix > 0 ? '+' : ''}${ecartPrix.toFixed(1)} %` : '—'}
            color={colorClass}
          />
        </div>

        {/* Donuts côte à côte sur desktop, empilés sur mobile */}
        {(donutData.length > 0 || donutRenovData.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Donut 1 — composition du prix de vente potentiel */}
            {donutData.length > 0 && (
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 flex flex-col items-center">
                <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-1 text-center">
                  Prix de vente potentiel ({fmtChf(prixVentePotentiel)})
                </p>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={donutData}
                      cx="50%" cy="50%"
                      innerRadius={42} outerRadius={68}
                      dataKey="value"
                      strokeWidth={2}
                      stroke="transparent"
                    >
                      {donutData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      formatter={(v: any) => fmtChf(Number(v))}
                      contentStyle={{ fontSize: 11, borderRadius: 8 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-0.5 mt-2 w-full">
                  {donutData.map(d => {
                    const pct = prixVentePotentiel > 0
                      ? (d.value / prixVentePotentiel * 100).toFixed(1) : '0';
                    return (
                      <div key={d.name} className="flex items-center justify-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: d.fill }} />
                        <span>{d.name} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Donut 2 — composition des travaux de rénovation */}
            {donutRenovData.length > 0 && (
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 flex flex-col items-center">
                <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-1 text-center">
                  Travaux de rénovation ({fmtChf(totalRenovation)})
                </p>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={donutRenovData}
                      cx="50%" cy="50%"
                      innerRadius={42} outerRadius={68}
                      dataKey="value"
                      strokeWidth={2}
                      stroke="transparent"
                    >
                      {donutRenovData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      formatter={(v: any) => fmtChf(Number(v))}
                      contentStyle={{ fontSize: 11, borderRadius: 8 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-0.5 mt-2 w-full">
                  {donutRenovData.map(d => {
                    const pct = totalRenovation > 0
                      ? (d.value / totalRenovation * 100).toFixed(1) : '0';
                    return (
                      <div key={d.name} className="flex items-center justify-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: d.fill }} />
                        <span>{d.name} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        )}

        {/* Signal bandeau */}
        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border ${bgClass}`}>
          {signalText}
        </div>
      </div>

      {/* ── Accordéon : Paramètres du scénario ─────────────────── */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
        <AccordionHeader
          title="Paramètres du scénario"
          open={showParams}
          onToggle={() => setShowParams(v => !v)}
        />
        {showParams && (
          <div className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-gray-800 pt-3">

            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Prix de vente</p>
              <NumInput
                label={`Prix moyen local /m²${marketPerM2 ? ` (${property.saleEstimateSource === 'realadvisor' ? 'RealAdvisor' : 'CMA local'} : ${marketPerM2} CHF)` : ' — saisir manuellement'}`}
                value={prixMarche}
                onChange={handlePrixMarcheChange}
                unit="CHF/m²"
              />
              <NumInput
                label="Facteur de vente potentielle"
                value={facteur}
                onChange={setFacteur}
                unit={`= ${Math.round(facteur * 100)}%`}
                step={0.05}
              />
              <NumInput
                label="Marge souhaitée sur prix de vente"
                value={margeVoulue}
                onChange={setMargeVoulue}
                unit="%"
                step={1}
              />
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Forfaits fixes</p>
              <NumInput label="Cuisine"       value={cuisine}   onChange={setCuisine}   unit="CHF" />
              <NumInput label="Salle de bain" value={salleBain} onChange={setSalleBain} unit="CHF" />
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Coûts unitaires</p>
              <NumInput label="Menuiserie"  value={menuiserie}  onChange={setMenuiserie}  unit="CHF/pièce" />
              <NumInput label="Sol"         value={sol}         onChange={setSol}         unit="CHF/m²" />
              <NumInput label="Électricité" value={electricite} onChange={setElectricite} unit="CHF/m²" />
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Frais</p>
              <NumInput label="Frais d'achat"                   value={fraisAchat}   onChange={setFraisAchat}   unit="%" step={0.5} />
              <NumInput label="Frais de courtage"              value={fraisRevente} onChange={setFraisRevente} unit="%" step={0.5} />
              <NumInput label="Intérêts emprunt rénovation (an)" value={fraisEmprunt} onChange={setFraisEmprunt} unit="%" step={0.5} />
            </div>
          </div>
        )}
      </div>

      {/* ── Accordéon : Détail des calculs ──────────────────────── */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
        <AccordionHeader
          title="Détail des calculs"
          open={showDetail}
          onToggle={() => setShowDetail(v => !v)}
        />
        {showDetail && (
          <div className="px-4 pb-4 divide-y divide-gray-100 dark:divide-gray-800 border-t border-gray-100 dark:border-gray-800">

            <div className="py-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Prix de vente potentiel</p>
              <Row label={`Surface (${surface} m²) × ${prixMarche} CHF/m² × ${Math.round(facteur * 100)}%`} value={fmtChf(prixVentePotentiel)} bold />
            </div>

            <div className="py-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Prix de vente net</p>
              <Row label="Prix de vente potentiel"                  value={fmtChf(prixVentePotentiel)} indent />
              <Row label={`Frais de courtage (${fraisRevente}%)`} value={`− ${fmtChf(fraisReventeMontant)}`} indent />
              <Row label="Prix de vente net"                     value={fmtChf(prixVenteNet)} bold />
            </div>

            <div className="py-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Coûts des travaux de rénovation</p>
              <Row label="Cuisine"                                                    value={fmtChf(cuisine)}            indent />
              <Row label="Salle de bain"                                              value={fmtChf(salleBain)}          indent />
              <Row label={`Menuiserie (${rooms} pièces × ${menuiserie} CHF)`}         value={fmtChf(coutMenuiserieTotal)} indent />
              <Row label={`Sol (${surface} m² × ${sol} CHF)`}                         value={fmtChf(coutSolTotal)}       indent />
              <Row label={`Électricité (${surface} m² × ${electricite} CHF)`}         value={fmtChf(coutElecTotal)}      indent />
              <Row label="Total travaux"                                              value={fmtChf(totalRenovation)} bold />
            </div>

            <div className="py-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Marge souhaitée</p>
              <Row label={`${margeVoulue}% × prix de vente potentiel`} value={fmtChf(margeSouhaitee)} bold />
            </div>

            <div className="py-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Frais financiers</p>
              <Row label={`Frais d'achat (${fraisAchat}% × prix d'achat max)`}       value={fmtChf(fraisAchatMontant)}   indent />
              <Row label={`Intérêts emprunt rénovation (${fraisEmprunt}% × travaux)`} value={fmtChf(fraisEmpruntMontant)} indent />
              <Row label="Total frais financiers"                                     value={fmtChf(totalFraisFinanciers)} bold />
            </div>

            <div className="py-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Total des coûts</p>
              <Row label="Travaux + Marge + Frais financiers" value={fmtChf(totalCouts)} bold />
            </div>

            <div className="py-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Prix d'achat maximum</p>
              {prixAchatMax != null ? (
                <Row
                  label="Prix de vente net − Total des coûts"
                  value={fmtChf(prixAchatMax)}
                  bold
                  color={colorClass}
                />
              ) : (
                <p className="text-sm text-gray-400 italic">Données insuffisantes</p>
              )}
            </div>

            <div className="py-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">ROI sur capital investi</p>
              <Row
                label="Marge / Capital investi"
                value={`${roi.toFixed(1)} %`}
                bold
                color={colorClass}
              />
            </div>

            <div className="py-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Prix demandé vs prix maximum</p>
              <Row label="Prix demandé" value={fmtChf(prixAchat)} indent />
              {ecartPrix != null && (
                <Row
                  label={ecartPrix > 0 ? 'Au-dessus du prix max' : 'En-dessous du prix max'}
                  value={`${ecartPrix > 0 ? '+' : ''}${ecartPrix.toFixed(1)} %`}
                  bold
                  color={colorClass}
                />
              )}
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400">
        Les paramètres sont sauvegardés automatiquement pour cette annonce. Pour changer les valeurs par défaut, rendez-vous dans{' '}
        <a href="/profil-investisseur" className="text-blue-500 hover:underline">Profil Investisseur → Étape 2</a>.
      </p>
    </div>
  );
};
